import React, { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Notifications } from "@mantine/notifications";

import "./App.css";

import { Sidebar } from "./pages/dashboard/components/orgnims/Sidebar";
import { Header } from "./pages/dashboard/components/orgnims/Header";
import { cvReturnPath, expireSession, isTokenExpired } from "./pages/config/session";
import { isAdminHost } from "./pages/config/hosts";
import NotFoundPage from "./pages/notfound/NotFoundPage";
import mtnLogo from "./assets/brand/mtn-logo.jpg";

/* Route-level code splitting keeps initial load small; pages load on demand. */
const InternalLogin = lazy(() => import("./pages/login/InternalLogin"));
const Dashboard = lazy(() =>
  import("./pages/dashboard/components/orgnims/Dashboard").then((m) => ({ default: m.Dashboard }))
);
const RecruitmentPage = lazy(() =>
  import("./pages/form/component/orgnims/RecruitmentPage").then((m) => ({
    default: m.RecruitmentPage,
  }))
);
const Inbox = lazy(() =>
  import("./pages/inbox/components/orgnims/Inbox").then((m) => ({ default: m.Inbox }))
);
const Sent = lazy(() =>
  import("./pages/sent/components/orgnims/Sent").then((m) => ({ default: m.Sent }))
);
const TablePages = lazy(() => import("./pages/tables/components/orgnims/TablePages"));
const ShortlistPage = lazy(() => import("./pages/shortlisting/components/orgnims/ShortlistPage"));
const MarksPage = lazy(() => import("./pages/marks/components/orgnims/MarksPage"));
const RequestPage = lazy(() =>
  import("./pages/requestform/components/organims/RequestPage").then((m) => ({
    default: m.RequestPage,
  }))
);
const ArchivedPage = lazy(() => import("./pages/Archived/components/organims/ArchivedPage"));
const CandidatePage = lazy(() => import("./pages/candidates/components/organims/CandidatePage"));
const PendingPage = lazy(() => import("./pages/Pending/orgnims/PendingPage"));
const CareerFormPage = lazy(() => import("./pages/vacancy/component/orgnims/CareerFormPage"));
const JobListingPage = lazy(() => import("./pages/careers/components/orgamins/JobListingPage"));
const UnsubscribePage = lazy(() => import("./pages/careers/components/orgamins/UnsubscribePage"));
const JobDetailsPage = lazy(() => import("./pages/details/component/orgnims/JobDetailsPage"));
const ApprovalPage = lazy(() => import("./pages/approval/components/organisms/ApprovalPage"));
const Candidatelist = lazy(() =>
  import("./pages/candidateDetails/components/orgnisms/Candidatelist")
);
const CandidateDashboard = lazy(() =>
  import("./pages/viewcandidates/components/organisms/CandidateDashboard").then((m) => ({
    default: m.CandidateDashboard,
  }))
);
const ExamPage = lazy(() =>
  import("./pages/exam/components/organims/ExamPage").then((m) => ({ default: m.ExamPage }))
);
const ApplicationForm = lazy(() => import("./pages/Apply/components/orgamins/ApplicationForm"));
const UsersPage = lazy(() => import("./pages/users/UsersPage"));
const EmailsPage = lazy(() => import("./pages/emails/EmailsPage"));
const SubscribersPage = lazy(() => import("./pages/subscribers/SubscribersPage"));
const CvViewPage = lazy(() => import("./pages/candidates/components/organims/CvViewPage"));
const OngoingHiresTablePage = lazy(() =>
  import("./pages/dashboard/components/orgnims/SummaryItemsPage").then((m) => ({
    default: m.OngoingHiresTablePage,
  }))
);
const CompletedHiresTablePage = lazy(() =>
  import("./pages/dashboard/components/orgnims/SummaryItemsPage").then((m) => ({
    default: m.CompletedHiresTablePage,
  }))
);
const DiscardedHiresTablePage = lazy(() =>
  import("./pages/dashboard/components/orgnims/SummaryItemsPage").then((m) => ({
    default: m.DiscardedHiresTablePage,
  }))
);

const PageLoader = () => (
  <div className="flex min-h-[40vh] flex-1 items-center justify-center text-sm text-gray-500">
    Loading…
  </div>
);

const ProtectedRoute = () => {
  if (!isAdminHost()) return <NotFoundPage />;
  const token = localStorage.getItem("token");
  if (token && isTokenExpired(token)) {
    expireSession();
    return null;
  }
  if (token) return <Outlet />;
  const back = cvReturnPath(`${window.location.pathname}${window.location.search}`);
  const login = back ? `/login_01?next=${encodeURIComponent(back)}` : "/login_01";
  return <Navigate to={login} replace />;
};

const DashboardLayout = () => (
  <div className="flex h-screen overflow-hidden bg-gray-100">
    <Sidebar />
    <div className="flex flex-col flex-1 h-screen ml-auto">
      <Header logoSrc={mtnLogo} logoAlt="MTN" />
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    document.title = "MTN Recruitment";
  }, []);

  return (
    <Router>
      <Notifications position="top-right" />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<JobListingPage />} />
          <Route path="/career" element={<Navigate to="/" replace />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/apply/:jobid" element={<ApplicationForm />} />
          <Route path="/jobs/:jobId" element={<JobDetailsPage />} />
          <Route path="/cv" element={<CvViewPage />} />
          {isAdminHost() ? <Route path="/login_01" element={<InternalLogin />} /> : null}

          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/recruitment" element={<RecruitmentPage />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/sent" element={<Sent />} />
              <Route path="/CandDashboard/:candidateId" element={<CandidateDashboard />} />
              <Route path="/tables" element={<TablePages />} />
              <Route path="/shortlist/:jobId" element={<ShortlistPage />} />
              <Route path="/marks" element={<MarksPage />} />
              <Route path="/exam" element={<ExamPage />} />
              <Route path="/requests/:id" element={<RequestPage />} />
              <Route path="/archive" element={<ArchivedPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/emails" element={<EmailsPage />} />
              <Route path="/subscribers" element={<SubscribersPage />} />
              <Route path="/jobs/:jobId/candidates" element={<CandidatePage />} />
              <Route path="/pending" element={<PendingPage />} />
              <Route path="/approval" element={<ApprovalPage />} />
              <Route path="/vacancy/:requestId" element={<CareerFormPage />} />
              <Route path="/advertise" element={<CareerFormPage />} />
              <Route path="/career/edit/:jobId" element={<CareerFormPage />} />
              <Route path="/candidate" element={<Candidatelist />} />
              <Route path="/requests/ongoing-hires" element={<OngoingHiresTablePage />} />
              <Route path="/requests/completed-hires" element={<CompletedHiresTablePage />} />
              <Route path="/requests/discarded-hires" element={<DiscardedHiresTablePage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
